import { useState, useEffect } from "react";
import { app, authentication } from "@microsoft/teams-js";
import { msalInstance } from "./msalConfig";
import { AccountInfo } from "@azure/msal-browser";

/**
 * Detects if running inside Microsoft Teams and performs SSO authentication.
 * When inside Teams, uses Teams SDK getAuthToken() to get an ID token silently,
 * then feeds it to MSAL so the rest of the app (Convex auth) works normally.
 */
export function useTeamsSSO() {
  const [isInTeams, setIsInTeams] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // Wait for MSAL to be ready
        await msalInstance.initialize();

        console.log("[Teams SSO] Checking if running in Teams...");
        // Teams SDK initialize() hangs in non-Teams browsers — add timeout
        await Promise.race([
          app.initialize(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Teams SDK timeout")), 3000)),
        ]);
        const context = await app.getContext();

        if (cancelled) return;

        const hostName = context?.app?.host?.name;
        console.log("[Teams SSO] Host:", hostName);

        if (hostName) {
          setIsInTeams(true);
          await performSSO(context);
        }
      } catch (err) {
        // Not in Teams — normal browser flow
        console.log("[Teams SSO] Not in Teams:", err instanceof Error ? err.message : err);
        if (!cancelled) setIsInTeams(false);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    }

    async function performSSO(context: Awaited<ReturnType<typeof app.getContext>>) {
      try {
        // Check if MSAL already has a cached account
        const existingAccounts = msalInstance.getAllAccounts();
        if (existingAccounts.length > 0) {
          console.log("[Teams SSO] MSAL already has cached account:", existingAccounts[0].username);
          msalInstance.setActiveAccount(existingAccounts[0]);
          return;
        }

        // Step 1: Get SSO token from Teams — this is silent, no popup
        console.log("[Teams SSO] Requesting Teams SSO token...");
        const token = await authentication.getAuthToken();
        console.log("[Teams SSO] Got Teams token, length:", token.length);

        const claims = parseJwt(token);
        const loginHint = claims.upn || claims.preferred_username || context.user?.loginHint || "";
        console.log("[Teams SSO] Login hint:", loginHint);

        // Step 2: Use ssoSilent to exchange for MSAL session
        console.log("[Teams SSO] Calling ssoSilent...");
        const response = await msalInstance.ssoSilent({
          scopes: ["openid", "profile", "email"],
          loginHint,
        });

        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          console.log("[Teams SSO] SSO success:", response.account.username);
        }
      } catch (ssoErr) {
        const msg = ssoErr instanceof Error ? ssoErr.message : String(ssoErr);
        console.warn("[Teams SSO] SSO failed:", msg);
        if (!cancelled) setSsoError(msg);

        // Fallback: try loginPopup inside Teams
        try {
          console.log("[Teams SSO] Trying loginPopup fallback...");
          const response = await msalInstance.loginPopup({
            scopes: ["openid", "profile", "email"],
          });
          if (response?.account) {
            msalInstance.setActiveAccount(response.account);
            console.log("[Teams SSO] Popup login success:", response.account.username);
          }
        } catch (popupErr) {
          console.error("[Teams SSO] Popup also failed:", popupErr);
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  return { isInTeams, isInitializing, ssoError };
}

/** Decode JWT payload without verification (client-side only) */
function parseJwt(token: string): Record<string, string> {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}
